import React, { useEffect, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";
import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { unmaskPostalCode } from "../../u2000Core/tools/U2000MaskUtils";
import { NS, U3121ROLES } from "../U3121";
import U3121SubProjectBody from "./form/u3121SubProject/U3121SubProjectBody";
import { U3121SubProjectFormToolBar } from "./form/u3121SubProject/U3121SubProjectFormToolBar";
import U3121SubProjectDeleteDlg from "./common/U3121SubProjectDeleteDlg";
import { useU3121Context } from "../U3121Context";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import U3121SubProjectBillingProfileBody from "./form/u3121SubProject/u3121SubProjectBillingProfileTab/U3121SubProjectBillingProfileBody";
import U3121SubProjectProjectToSurveyBody from "./form/u3121SubProject/u3121SubProjectProjectToSurveyTab/U3121SubProjectProjectToSurveyBody";
import U3121SubProjectEmployeeAssignmentBody from "./form/u3121SubProject/u3121SubProjectEmployeeAssignmentTab/U3121SubProjectEmployeeAssignmentBody";
import U3121SubProjectTimesheetAccessBody from "./form/u3121SubProject/u3121SubProjectTimesheetAccessTab/U3121SubProjectTimesheetAccessBody";
import U3121SubProjectScopePeriodBody from "./form/u3121SubProject/u3121SubProjectScopePeriodTab/U3121SubProjectScopePeriodBody";
import U3121SubProjectTaskAssignmentBody from "./form/u3121SubProject/u3121SubProjectTaskAssignmentTab/U3121SubProjectTaskAssignmentBody";
import U3121SubProjectWorkNatureAssignmentBody from "./form/u3121SubProject/u3121SubProjectWorkNatureAssignmentTab/U3121SubProjectWorkNatureAssignmentBody";
import {
  doChangeStatusSubProject,
  doCreateSubProjectData,
  doDeleteSubProjectData,
  doGetData,
  doGetSubProjectAuditsData,
  doGetSubProjectData,
  doUpdateSubProjectData,
  emptyBillingProfile,
  emptyProjectToSurvey,
  normalizeProjectToSurveyFromApi,
  emptySubProject,
  luHandleAppiError,
  normalizeBillingProfileFromApi,
  subProjectPayloadForSave,
  type U3121Project,
  type U3121SubProject,
} from "../U3121Service";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import {
  mergeGoogleCalendarSyncFromWs,
  useU3121GoogleCalendarSyncNotification,
} from "../U3121UseGoogleCalendarSyncNotification";

const U3121SubProjectForm: React.FC = () => {
  const { projectId, subProjectId } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- useReducer action type must satisfy React's AnyActionArg; U2000FormAction is incompatible
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { initLU, searchStateHandlers, gridProjectSubProjectStateHandlers, luStateHandlers } = useU3121Context();
  const handleGetRef = useRef<((pid: string, sid: string) => Promise<void>) | null>(null);

  const validateForm = (sub: U3121SubProject) => {
    const result: Record<string, string> = {};
    if (!sub.subProjetNumber?.trim()) result["subProjetNumber"] = t("errSubProjetNumberRequired", { ns: NS });
    if (sub.subProjetNumber && sub.subProjetNumber.length > 15) result["subProjetNumber"] = t("errSubProjetNumberMaxLength", { ns: NS });
    if (!sub.subProjetName?.trim()) result["subProjetName"] = t("errSubProjetNameRequired", { ns: NS });
    if (sub.subProjetName && sub.subProjetName.length > 255) result["subProjetName"] = t("errSubProjetNameMaxLength", { ns: NS });
    // Validation du profil de facturation (alignée sur le projet : mêmes règles et clés)
    const bp = sub.billingProfile;
    if (bp?.billable && !bp?.hourly && !bp?.fixedPrice) {
      result["billingProfile.hourly"] = t("errBillableRequiresHoraireOrForfaitaire", { ns: NS });
    }
    if (bp?.fixedPrice && (bp?.fixedAmount ?? 0) <= 0) {
      result["billingProfile.fixedAmount"] = t("errMontantForfaitaireMustBePositive", { ns: NS });
    }
    if (bp?.billable && bp?.hourly && (bp?.sellingRate == null || Number(bp.sellingRate) <= 0)) {
      result["billingProfile.sellingRate"] = t("errSellingRateRequired", { ns: NS });
    }
    if (bp?.billable && (bp?.hourlyMarkup == null || Number(bp.hourlyMarkup) < 1)) {
      result["billingProfile.hourlyMarkup"] = t("errHourlyMarkupMinOne", { ns: NS });
    }
    if (bp?.billable && bp.billingFrequency !== "EndProject" && bp.billingFrequency !== "ByStep") {
      result["billingProfile.billingFrequency"] = t("errBillingFrequencyRequired", { ns: NS });
    }
    // Valider le projet à arpenter uniquement si includeProjectToSurvey
    if (sub.includeProjectToSurvey && sub.projectToSurvey) {
      const pts = sub.projectToSurvey;
      const hasPts = pts && typeof pts === "object";
      const lotArr = Array.isArray(pts.lot) ? pts.lot : [];
      if (!hasPts || lotArr.length === 0 || !lotArr.some((s) => String(s ?? "").trim())) result["projectToSurvey.lot"] = t("errProjectToSurveyLotRequired", { ns: NS });
      if (!hasPts || !pts.owner?.trim()) result["projectToSurvey.owner"] = t("errProjectToSurveyOwnerRequired", { ns: NS });
      if (!hasPts || !pts.address?.trim()) result["projectToSurvey.address"] = t("errProjectToSurveyAddressRequired", { ns: NS });
      if (!hasPts || !pts.city?.trim()) result["projectToSurvey.city"] = t("errProjectToSurveyCityRequired", { ns: NS });
      const postalUnmasked = hasPts && pts.postalCode != null ? unmaskPostalCode(pts.postalCode) : "";
      if (postalUnmasked.length > 0 && postalUnmasked.length !== 6) {
        result["projectToSurvey.postalCode"] = t("errProjectToSurveyPostalCodeInvalid", { ns: NS });
      }
      if (!hasPts || !pts.country?.id?.toString().trim()) result["projectToSurvey.country.id"] = t("errProjectToSurveyCountryRequired", { ns: NS });
      if (!hasPts || !pts.state?.id?.toString().trim()) result["projectToSurvey.state.id"] = t("errProjectToSurveyStateRequired", { ns: NS });
      if (!hasPts || !pts.dueDate?.trim()) result["projectToSurvey.dueDate"] = t("errProjectToSurveyDueDateRequired", { ns: NS });
      if (!hasPts || !pts.dueDateType) result["projectToSurvey.dueDateType"] = t("errProjectToSurveyDueDateTypeRequired", { ns: NS });
    }
    // Valider l'assignation des tâches uniquement si includeTaskAssignments
    if (sub.includeTaskAssignments && (!sub.taskAssignments || sub.taskAssignments.length === 0)) {
      result["taskAssignments"] = t("errTaskAssignmentRequired", { ns: NS });
    }
    return result;
  };

  /** Retourne l’id du premier onglet contenant des erreurs (pour y naviguer après validation). */
  const getFirstTabWithErrors = (errs: Record<string, string>): string | null => {
    if (Object.keys(errs).some((k) => k.startsWith("billingProfile."))) return "tabBillingProfile";
    if (Object.keys(errs).some((k) => k.startsWith("projectToSurvey."))) return "tabProjectToSurvey";
    if (errs["taskAssignments"]) return "tabTaskAssignment";
    return null;
  };

  const businessHandlers: U2000FormSBusinessHandlers<U3121SubProject> = {
    newRecord: (): U3121SubProject => {
      const defaultBilling = emptySubProject.billingProfile ?? emptyBillingProfile;
      const orgHourlyMarkup = initLU.personalization?.orgHourlyMarkup;
      const n = orgHourlyMarkup != null && orgHourlyMarkup !== "" ? Number(orgHourlyMarkup) : NaN;
      const hourlyMarkupDefault = !Number.isNaN(n) ? n : (defaultBilling.hourlyMarkup ?? 0);
      return {
        ...emptySubProject,
        projectId: projectId ?? "",
        billingProfile: { ...defaultBilling, id: defaultBilling.id ?? "", hourlyMarkup: hourlyMarkupDefault },
      };
    },
    validateForm,
    saveForme: async (sub: U3121SubProject, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(sub);
      if (Object.keys(errors).length === 0) handleSave.saveData(sub, editMode);
      else {
        formStateHandlers.setSubmitted(true);
        formStateHandlers.setErrors(errors);
        const firstTabWithErrors = getFirstTabWithErrors(errors);
        if (firstTabWithErrors) setActiveTab(firstTabWithErrors);
      }
    },
    cancelForm: () => {
      if (projectId) {
        try {
          sessionStorage.setItem("u3121_refetch_project", "1");
        } catch {
          // ignore
        }
        // Retour au formulaire projet uniquement si on est venu depuis l'onglet Sous-projets du projet (modèle U2104 Role).
        if (luStateHandlers.getActionOriginFrom() === "/U3121/PROJECT") {
          navigate(`/U3121/${projectId}`, { replace: true });
        }
      }
    },
    onChangeRecordForm: (record: U3121SubProject) => {
      if (record?.id && projectId) {
        navigate(`/U3121/${projectId}/subproject/${record.id}`, { replace: true });
        handleGetRef.current?.(projectId, record.id);
      }
    },
    onChangePageForm: () => {},
    onLeaveForm: () => {
      if (projectId) navigate(`/U3121/${projectId}`, { replace: true });
    },
  };

  const formStateHandlers = useU2000FormStateHandlers<U3121SubProject>({
    formState,
    formStateDispatch,
    businessHandlers,
  });

  const changeStatusSubProject = async () => {
    const sub = formStateHandlers.getData();
    if (!projectId || !subProjectId || subProjectId === "create" || !sub?.id) return;
    const status = sub.subProjectStatus ? "Close" : "Reopen";
    try {
      const response = await doChangeStatusSubProject(u2000AxiosAuthenticated, projectId, subProjectId, status);
      const data = response.data as { result?: U3121SubProject } & U3121SubProject;
      const updated = (data?.result ?? data) as U3121SubProject;
      if (!updated) return;
      if (updated.includeBillingProfile === undefined) updated.includeBillingProfile = true;
      if (updated.includeEmployeeBillingAssignments === undefined) updated.includeEmployeeBillingAssignments = (updated.employeeBillingAssignments?.length ?? 0) > 0;
      if (updated.includeTaskAssignments === undefined) updated.includeTaskAssignments = (updated.taskAssignments?.length ?? 0) > 0 || Boolean(updated.taskAssignment);
      if (updated.includeWorkNatureAssignments === undefined) updated.includeWorkNatureAssignments = (updated.workNatureAssignments?.length ?? 0) > 0;
      if (updated.includeProjectToSurvey === undefined) updated.includeProjectToSurvey = updated.projectToSurvey != null;
      if (updated.billingProfile) {
        normalizeBillingProfileFromApi(updated.billingProfile);
      } else {
        updated.billingProfile = { ...emptyBillingProfile };
      }
      if (!updated.employeeBillingAssignments) updated.employeeBillingAssignments = updated.employeeBillingAssignment ? [updated.employeeBillingAssignment] : [];
      if (!updated.taskAssignments) updated.taskAssignments = updated.taskAssignment ? [{ id: (updated.taskAssignment as { id: string }).id }] : [];
      if (!updated.workNatureAssignments) updated.workNatureAssignments = [];
      if (!updated.projectToSurvey) updated.projectToSurvey = { ...emptyProjectToSurvey };
      normalizeProjectToSurveyFromApi(updated.projectToSurvey ?? undefined);
      formStateHandlers.setData(updated);
      gridProjectSubProjectStateHandlers.setData([updated]);
      gridProjectSubProjectStateHandlers.setTotalRows(1);
      gridProjectSubProjectStateHandlers.setCurrentIdx(0);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
    }
  };

  const extendedHandlers = {
    ...formStateHandlers,
    addNewSubProject: () => formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD),
    modifySubProject: () => formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT),
    deleteSubProject: () => handleDelete.handleDelete(),
    changeStatusSubProject,
  };

  const handleGet = async (pid: string, sid: string) => {
    try {
      const response = await doGetSubProjectData(u2000AxiosAuthenticated, pid, sid);
      const data = response.data as { result?: U3121SubProject } & U3121SubProject;
      const sub = (data?.result ?? data) as U3121SubProject;
      // Profil de facturation obligatoire pour le sous-projet : toujours true
      if (sub.includeBillingProfile === undefined) sub.includeBillingProfile = true;
      if (sub.includeEmployeeBillingAssignments === undefined) sub.includeEmployeeBillingAssignments = (sub.employeeBillingAssignments?.length ?? 0) > 0;
      if (sub.includeTaskAssignments === undefined) sub.includeTaskAssignments = (sub.taskAssignments?.length ?? 0) > 0 || Boolean(sub.taskAssignment);
      if (sub.includeWorkNatureAssignments === undefined) sub.includeWorkNatureAssignments = (sub.workNatureAssignments?.length ?? 0) > 0;
      if (sub.includeProjectToSurvey === undefined) sub.includeProjectToSurvey = sub.projectToSurvey != null;
      // Normaliser le profil de facturation pour les onglets (snake_case API → camelCase)
      if (sub.billingProfile) {
        normalizeBillingProfileFromApi(sub.billingProfile);
      } else {
        sub.billingProfile = { ...emptyBillingProfile };
      }
      if (!sub.employeeBillingAssignments) sub.employeeBillingAssignments = sub.employeeBillingAssignment ? [sub.employeeBillingAssignment] : [];
      if (!sub.taskAssignments) sub.taskAssignments = sub.taskAssignment ? [{ id: (sub.taskAssignment as { id: string }).id }] : [];
      if (!sub.workNatureAssignments) sub.workNatureAssignments = [];
      if (!sub.projectToSurvey) sub.projectToSurvey = { ...emptyProjectToSurvey };
      normalizeProjectToSurveyFromApi(sub.projectToSurvey ?? undefined);
      formStateHandlers.setData(sub);
      const currentGridData = gridProjectSubProjectStateHandlers.getData();
      const idx = currentGridData.findIndex((r) => r.id === sub.id);
      if (currentGridData.length > 1 && idx >= 0) {
        gridProjectSubProjectStateHandlers.setDataById(sub.id, sub);
        gridProjectSubProjectStateHandlers.setCurrentIdx(idx);
      } else {
        gridProjectSubProjectStateHandlers.setData([sub]);
        gridProjectSubProjectStateHandlers.setTotalRows(1);
        gridProjectSubProjectStateHandlers.setCurrentIdx(0);
      }
      // Si on est arrivé via « Modifier » (onglet sous-projets du formulaire projet), passer en mode édition et consommer nextAction pour que le formulaire projet ne passe pas en édition au retour.
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      }
      // Breadcrumb : afficher le no de projet (projetNumber), pas l'id. Si le GET sous-projet ne renvoie pas project, on charge le projet.
      const setBreadcrumb = (label: string) =>
        setLogicalUnitBreadcrumb([{ href: "/U3121", label: t("title", { ns: NS }) }, { href: `/U3121/${pid}`, label }, { label: `${sub.subProjetName ?? ""} (${sub.subProjetNumber ?? ""})` }]);
      if (sub.project?.projetNumber != null || sub.project?.projetName != null) {
        const projectLabel = `${sub.project.projetName ?? ""} (${sub.project.projetNumber ?? ""})`.trim() || pid;
        setBreadcrumb(projectLabel);
      } else {
        doGetData(u2000AxiosAuthenticated, pid)
          .then((res) => {
            const project = res.data as U3121Project;
            const projectLabel = `${project.projetName ?? ""} (${project.projetNumber ?? ""})`.trim() || pid;
            setBreadcrumb(projectLabel);
          })
          .catch(() => setBreadcrumb(pid));
      }
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
    }
  };
  handleGetRef.current = handleGet;

  const handleSave = useU2000SaveHandlers<U3121SubProject>({
    doSaveFn: (sub: U3121SubProject, editMode: U2000FormStateEnumEditMode) => {
      if (!projectId) return Promise.reject(new Error("projectId missing"));
      const payload = subProjectPayloadForSave(sub, projectId);
      if (editMode === U2000FormStateEnumEditMode.ADD) return doCreateSubProjectData(u2000AxiosAuthenticated, projectId, payload);
      return doUpdateSubProjectData(u2000AxiosAuthenticated, projectId, sub.id, payload);
    },
    onSuccess: (sub: U3121SubProject & { id: string }, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridProjectSubProjectStateHandlers.setData([sub]);
        gridProjectSubProjectStateHandlers.setTotalRows(1);
        // Si on n'est pas venu du projet, rester sur le formulaire sous-projet avec le nouvel id (modèle U2104 Role).
        if (luStateHandlers.getActionOriginFrom() !== "/U3121/PROJECT" && projectId) {
          const newPath = location.pathname.replace(/create$/, sub.id);
          navigate(newPath, { replace: true });
        }
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridProjectSubProjectStateHandlers.setData([sub]);
      }
      formStateHandlers.afterSaveData(sub);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));
      if (projectId) {
        try {
          sessionStorage.setItem("u3121_refetch_project", "1");
        } catch {
          // ignore
        }
        // Retour au formulaire projet uniquement si on est venu depuis l'onglet Sous-projets du projet (modèle U2104 Role).
        if (luStateHandlers.getActionOriginFrom() === "/U3121/PROJECT") {
          navigate(`/U3121/${projectId}`, { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) formStateHandlers.setErrors(errorResult.fieldErrors);
      else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) formStateHandlers.setOtherErrors(errorResult.otherErrors);
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [formStateHandlers.getData()],
    doDeleteFn: async (_id, row) => {
      if (!projectId || !subProjectId) throw new Error("projectId or subProjectId missing");
      return doDeleteSubProjectData(u2000AxiosAuthenticated, projectId, subProjectId, row as U3121SubProject);
    },
    onSuccess: () => {
      toast.success(t("success0003", { key: (formStateHandlers.getData() as U3121SubProject)?.subProjetNumber, ns: NS }), t("successTitle", { ns: NS }));
      if (projectId) {
        try {
          sessionStorage.setItem("u3121_refetch_project", "1");
        } catch {
          // ignore
        }
        navigate(`/U3121/${projectId}`, { replace: true });
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      });
    },
  });

  useU3121GoogleCalendarSyncNotification({
    projectId,
    subProjectId: subProjectId && subProjectId !== "create" ? subProjectId : undefined,
    onSyncUpdated: (update) => {
      const current = formStateHandlers.getData();
      if (!current?.projectToSurvey) return;
      const mergedSync = mergeGoogleCalendarSyncFromWs(
        update.googleCalendarSync,
        update.dateDivergence,
        current.projectToSurvey.googleCalendarSync,
      );
      const projectDueDate = update.projectDueDate?.trim();
      formStateHandlers.setData({
        ...current,
        projectToSurvey: {
          ...current.projectToSurvey,
          ...(projectDueDate ? { dueDate: projectDueDate } : {}),
          googleCalendarSync: mergedSync ?? undefined,
        },
      });
    },
  });

  useEffect(() => {
    if (!projectId || hasInitialized) return;
    if (!subProjectId || subProjectId === "create") {
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
      const defaultBilling = emptySubProject.billingProfile ?? emptyBillingProfile;
      const orgHourlyMarkup = initLU.personalization?.orgHourlyMarkup;
      const n = orgHourlyMarkup != null && orgHourlyMarkup !== "" ? Number(orgHourlyMarkup) : NaN;
      const hourlyMarkupDefault = !Number.isNaN(n) ? n : (defaultBilling.hourlyMarkup ?? 0);
      formStateHandlers.setData({
        ...emptySubProject,
        projectId,
        billingProfile: { ...defaultBilling, id: defaultBilling.id ?? "", hourlyMarkup: hourlyMarkupDefault },
      });
      gridProjectSubProjectStateHandlers.setData([]);
      gridProjectSubProjectStateHandlers.setTotalRows(0);
      doGetData(u2000AxiosAuthenticated, projectId)
        .then((res) => {
          const project = res.data as U3121Project;
          setLogicalUnitBreadcrumb([
            { href: "/U3121", label: t("title", { ns: NS }) },
            { href: `/U3121/${projectId}`, label: `${project.projetName ?? ""} (${project.projetNumber ?? ""})` },
            { label: t("lblNewSubProject", { ns: NS }) },
          ]);
        })
        .catch(() => {});
    } else {
      handleGet(projectId, subProjectId);
    }
    setHasInitialized(true);
  }, [projectId, subProjectId, hasInitialized]);

  const isCreation = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD;
  const tabPriority = [
    "tabBillingProfile",
    "tabProjectToSurvey",
    "tabEmployeeAssignment",
    "tabTimesheetAccess",
    "tabScopePeriod",
    "tabWorkNatureAssignment",
    "tabTaskAssignment",
    "tabAudits",
  ];
  const billingProfileHourly = formStateHandlers.getData()?.billingProfile?.hourly === true;
  const availableTabIdsForCreation = React.useMemo(
    () =>
      [
        "tabBillingProfile",
        "tabProjectToSurvey",
        ...(billingProfileHourly ? ["tabEmployeeAssignment"] : []),
        "tabTimesheetAccess",
        "tabScopePeriod",
        "tabWorkNatureAssignment",
        "tabTaskAssignment",
        ...(hasRole(U3121ROLES.AUDITS) ? ["tabAudits"] : []),
      ],
    [hasRole, billingProfileHourly],
  );
  const firstTabForCreation = availableTabIdsForCreation.length > 0 ? (tabPriority.find((id) => availableTabIdsForCreation.includes(id)) ?? availableTabIdsForCreation[0]) : undefined;
  const hasRepositionedTabForCreationRef = useRef(false);
  useEffect(() => {
    if (!isCreation) {
      hasRepositionedTabForCreationRef.current = false;
      return;
    }
    if (!firstTabForCreation || hasRepositionedTabForCreationRef.current) return;
    hasRepositionedTabForCreationRef.current = true;
    setActiveTab(firstTabForCreation);
  }, [isCreation, firstTabForCreation]);

  if (formStateHandlers.getData() === null) return null;

  /** Onglet Taux horaire : pertinent seulement si facturation horaire sur le sous-projet. */
  const showEmployeeAssignmentTab = billingProfileHourly;

  const doGetSubProjectAuditsWrapper = (_axios: unknown, id: string | number) =>
    projectId ? doGetSubProjectAuditsData(u2000AxiosAuthenticated, projectId, id) : Promise.reject(new Error("projectId missing"));

  const formTabs = [
    {
      id: "tabBillingProfile",
      label: t("tabBillingProfile", { ns: NS }),
      content: (
        <U3121SubProjectBillingProfileBody
          formStateHandlers={extendedHandlers as never}
          t={t}
          personalization={initLU.personalization}
          onValidateAfterBillingChange={(data) => {
            const errors = validateForm((data ?? formStateHandlers.getData()) as U3121SubProject);
            formStateHandlers.setErrors(errors);
          }}
        />
      ),
    },
    {
      id: "tabProjectToSurvey",
      label: t("tabProjectToSurvey", { ns: NS }),
      content: <U3121SubProjectProjectToSurveyBody formStateHandlers={extendedHandlers as never} t={t} />,
    },
    showEmployeeAssignmentTab && {
      id: "tabEmployeeAssignment",
      label: t("tabEmployeeAssignment", { ns: NS }),
      content: <U3121SubProjectEmployeeAssignmentBody formStateHandlers={extendedHandlers as never} t={t} />,
    },
    {
      id: "tabWorkNatureAssignment",
      label: t("tabWorkNatureAssignment", { ns: NS }),
      content: (
        <U3121SubProjectWorkNatureAssignmentBody
          formStateHandlers={extendedHandlers as never}
          t={t}
          onValidateAfterWorkNatureChange={(sub) => {
            const errors = validateForm(sub);
            formStateHandlers.setErrors(errors);
          }}
          onValidateAfterTaskChange={(sub) => {
            const errors = validateForm(sub);
            formStateHandlers.setErrors(errors);
          }}
        />
      ),
    },
    {
      id: "tabTaskAssignment",
      label: t("tabTaskAssignment", { ns: NS }),
      content: (
        <U3121SubProjectTaskAssignmentBody
          formStateHandlers={extendedHandlers as never}
          t={t}
          onValidateAfterTaskChange={(sub) => {
            const errors = validateForm(sub);
            formStateHandlers.setErrors(errors);
          }}
        />
      ),
    },
    {
      id: "tabTimesheetAccess",
      label: t("tabTimesheetAccess", { ns: NS }),
      content: <U3121SubProjectTimesheetAccessBody formStateHandlers={extendedHandlers} />,
    },
    {
      id: "tabScopePeriod",
      label: t("tabScopePeriod", { ns: NS }),
      content: <U3121SubProjectScopePeriodBody formStateHandlers={extendedHandlers} />,
    },

    hasRole(U3121ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={formStateHandlers.getData()?.id} doGetAuditsData={doGetSubProjectAuditsWrapper} toastName="U3121SubProjectAudit" />,
    },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  const errors = formStateHandlers.getErrors();
  const tabsWithErrors: string[] = [];
  if (Object.keys(errors).some((k) => k.startsWith("billingProfile."))) tabsWithErrors.push("tabBillingProfile");
  if (errors["taskAssignments"]) tabsWithErrors.push("tabTaskAssignment");
  if (Object.keys(errors).some((k) => k.startsWith("projectToSurvey."))) tabsWithErrors.push("tabProjectToSurvey");

  const availableTabIds = formTabs.map((tab) => tab.id);
  const firstAvailableTab = tabPriority.find((tabId) => availableTabIds.includes(tabId)) ?? availableTabIds[0];
  const defaultTab = isCreation ? firstAvailableTab : activeTab && availableTabIds.includes(activeTab) ? activeTab : firstAvailableTab;

  return (
    <>
      <U3121SubProjectDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000DefaultForm
        actionsBtns={<U3121SubProjectFormToolBar formStateHandlers={extendedHandlers as never} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridProjectSubProjectStateHandlers as never}
        formStateHandlers={extendedHandlers as never}
      >
        <U3121SubProjectBody formStateHandlers={extendedHandlers as never} t={t} />
        {formTabs.length > 0 && (
          <U2000Tab
            className="mt-4"
            tabs={formTabs}
            defaultTab={defaultTab}
            activeTab={activeTab ?? defaultTab}
            setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
            tabsWithErrors={tabsWithErrors}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3121SubProjectForm;
